import React, { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import delete2 from "../../Images/delete2.png";
import swal from "sweetalert";
import { deleteWarningMsg } from "../../config/messageconstant";
import { apiGet } from "../../utils/api_service";
import endpoint from "../../config/endpoints";
import { showLoador, showNotification } from "../Landing/Landing.slice";
import { connect } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = ({ children, onClose, ...other }) => (
  <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
    {children}
    {onClose && (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    )}
  </DialogTitle>
);

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const TableWithNewFunctionality = ({ showLoador, showNotification }) => {
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [searchQueryWIP, setSearchQueryWIP] = useState("");
  const [searchQueryComplete, setSearchQueryComplete] = useState("");
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [newField, setNewField] = useState({
    displayName: "",
    functionName: "",
    category: "",
  });

  const operators = [
    { value: "+", label: "Addition (+)" },
    { value: "-", label: "Subtraction (-)" },
    { value: "*", label: "Multiplication (*)" },
    { value: "/", label: "Division (/)" },
  ];

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        showLoador({ loador: true });
        const { data, success } = await apiGet({ url: endpoint.dynamicTableMaster });
        showLoador({ loador: false });

        if (success) {
          const dataList = data.data.content.map((dataObj, index) => ({
            index: index + 1,
            id: dataObj.id,
            name: dataObj.columnName,
            category: dataObj.category,
            isDraggable: dataObj.isDraggable == 1,
            isNumeric: dataObj.isNumeric == 1,
            displayName: dataObj.displayName,
            functionName: dataObj.functionName,
            isFilter: dataObj.isFilter,
            isOutput: dataObj.isOutput,
            isSort: dataObj.isSort,
            showTotal: dataObj.showTotal,
          }));
          setTasks(dataList);
        } else {
          showNotification({ msg: "Failed to fetch tasks", severity: "error" });
        }
      } catch (error) {
        showLoador({ loador: false });
        showNotification({ msg: "An error occurred", severity: "error" });
      }
    };

    fetchTasks();
  }, []);

  // Drag and Drop Handlers
  const onDragStart = (ev, id) => {

    console.log("OnDragStart-" + id)
    const task = tasks.find((t) => t.id === id);
    if (task.isDraggable) {
      ev.dataTransfer.setData("taskId", id.toString());
      setDraggedTaskId(id);
    } else {
      ev.preventDefault();
    }
  };

  const onDropCategory = (ev, category) => {
    ev.preventDefault();
    const id = ev.dataTransfer.getData("taskId");
    console.log("Dropping task ID:", id, "into category:", category); // Debug log
    const task = tasks.find((t) => t.id === id);
    if (task) {
      setTasks((prev) =>
        prev.map((t) => (t.id === id ? { ...t, category } : t))
      );
    } else {
      console.warn("Task not found for ID:", id);
    }
  };

  const onDropTable = (ev) => {
    ev.preventDefault();
    const id = ev.dataTransfer.getData("taskId");

    console.log("onDropTable-" + id)
    const task = tasks.find((t) => t.id === id);
    if (task) {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, category: "complete" } : t
        )
      );
    } else {
      console.warn("Task not found for ID:", id);
    }
  };

  const onDragStartTableRow = (ev, id) => {
    const task = tasks.find((t) => t.id === id);
    if (task.isDraggable) {
      console.log("Dragging table row task ID:", id); // Debug log
      ev.dataTransfer.setData("taskId", id.toString()); // Ensure ID is a string
      setDraggedTaskId(id);
    } else {
      ev.preventDefault();
    }
  };

  const onDropTableRow = (targetId) => {
    if (draggedTaskId === null || draggedTaskId === targetId) return;

    console.log("Swapping task ID:", draggedTaskId, "with target ID:", targetId); // Debug log
    const completeTasks = tasks.filter((task) => task.category === "complete");
    const otherTasks = tasks.filter((task) => task.category !== "complete");
    const draggedIndex = completeTasks.findIndex((task) => task.id === draggedTaskId);
    const targetIndex = completeTasks.findIndex((task) => task.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      console.warn("Invalid drag or target index");
      return;
    }

    const swappedTasks = [...completeTasks];
    [swappedTasks[draggedIndex], swappedTasks[targetIndex]] = [
      swappedTasks[targetIndex],
      swappedTasks[draggedIndex],
    ];

    setTasks([...otherTasks, ...swappedTasks]);
    setDraggedTaskId(null);
  };

  // Task Deletion
  const rowDelete = (task) => {
    setTasks((prev) => {
      const updatedTasks = prev.filter((t) => t.id !== task.id);
      if (task.category === "complete" && task.isDraggable) {
        return [
          ...updatedTasks,
          {
            ...task,
            id: Math.max(...updatedTasks.map((t) => t.id), 0) + 1,
            category: "wip",
            isDraggable: true,
          },
        ];
      }
      return updatedTasks;
    });
  };

  const onClickDelete = (task) => {
    swal({
      title: "Are you sure?",
      text: deleteWarningMsg,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) rowDelete(task);
    });
  };

  // Modal Handlers
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setNewField({ displayName: "", functionName: "", category: "" });
    setOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewField((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (
      !newField.displayName.trim() ||
      !newField.category.trim() ||
      !newField.functionName.trim()
    ) {
      showNotification({ msg: "Please fill all details", severity: "warning" });
      return;
    }
    const newTask = {
      id: Math.max(...tasks.map((t) => t.id), 0) + 1,
      name: newField.displayName,
      functionName: newField.functionName,
      isDraggable: false,
      category: "complete",
      isNumeric: true,
      isFilter: true,
      isOutput: true,
      isSort: true,
      showTotal: true,
    };
    setTasks((prev) => [...prev, newTask]);
    handleClose();
  };

  // Filtered Tasks
  const tasksWIP = tasks
    .filter((task) => task.category === "wip")
    .filter((task) => task.name.toLowerCase().includes(searchQueryWIP.toLowerCase()));

  const tasksComplete = tasks
    .filter((task) => task.category === "complete")
    .filter((task) =>
      task.name.toLowerCase().includes(searchQueryComplete.toLowerCase())
    );

  // Styles
  const containerStyle = {
    width: "100%",
    display: "flex",
    justifyContent: "space-around",
    padding: "30px",
    fontFamily: "'Montserrat', sans-serif",
    backgroundColor: "#f0f2f5",
  };

  const cardStyle = {
    flex: 1,
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    padding: "20px",
    margin: "0 10px",
    minHeight: "250px",
    display: "flex",
    flexDirection: "column",
  };

  const cardHeaderStyle = {
    backgroundColor: "#d9d9d9",
    color: "#1a1a1a",
    padding: "16px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 600,
    borderTopRightRadius: "12px",
    borderTopLeftRadius: "12px",
  };

  const taskStyle = {
    backgroundColor: "#fff",
    padding: "12px 20px",
    margin: "8px 0",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    cursor: "grab",
    transition: "transform 0.2s, box-shadow 0.2s",
    fontFamily: "poppins",
    fontSize: "13px",
  };

  const nonDraggableTaskStyle = {
    ...taskStyle,
    cursor: "default",
  };

  const tableContainerStyle = {
    width: "100%",
    overflowX: "auto",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
    marginTop: "15px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    borderSpacing: 0,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "13px",
    minWidth: "600px",
  };

  const headerStyle = {
    backgroundColor: "#d9d9d9",
    color: "#1a1a1a",
    padding: "16px",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    fontFamily: "poppins",
  };

  const navbarHeaderStyle = {
    color: "#1a1a1a",
    padding: "16px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    fontFamily: "poppins",
  };

  const thTdStyle = {
    padding: "14px 20px",
    textAlign: "left",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e0e0e0",
    transition: "background-color 0.2s",
    fontFamily: "poppins",
  };

  const tableRowStyle = {
    cursor: "pointer",
    transition: "background-color 0.3s",
  };

  const theadStyle = {
    backgroundColor: "#F8FAFC",
    color: "#333",
    fontWeight: 600,
    fontSize: "13px",
  };

  const searchContainerStyle = {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    border: "1px solid #ccc",
    borderRadius: "4px",
    backgroundColor: "#fff",
  };

  const searchInputStyle = {
    height: "33px",
    width: "100%",
    padding: "4px 8px 4px 30px",
    outline: "none",
    fontSize: "13px",
    fontFamily: "'Montserrat', sans-serif",
    border: "none",
  };

  const searchIconStyle = {
    padding: "0 8px",
    color: "#888",
    display: "flex",
    alignItems: "center",
    fontSize: "36px",
  };

  return (
    <div style={containerStyle}>
      {console.log("WIP:", tasksWIP)}
      {console.log("Complete-", tasksComplete)}

      {/* WIP Column */}
      <div
        style={{ ...cardStyle, minWidth: "20%" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => onDropCategory(e, "wip")}
      >
        <div style={cardHeaderStyle}>Select Column</div>
        <Box sx={searchContainerStyle} mt={2}>
          <SearchIcon sx={searchIconStyle} />
          <InputBase
            placeholder="Search"
            value={searchQueryWIP}
            onChange={(e) => setSearchQueryWIP(e.target.value)}
            sx={searchInputStyle}
            aria-label="Search WIP tasks"
          />
        </Box>
        {tasksWIP.length > 0 ? (
          tasksWIP.map((task) => (
            <div
              key={task.id}
              draggable={task.isDraggable}
              onDragStart={(e) => onDragStart(e, task.id)}
              style={task.isDraggable ? taskStyle : nonDraggableTaskStyle}
              onMouseEnter={(e) =>
                task.isDraggable && (e.currentTarget.style.transform = "scale(1.02)")
              }
              onMouseLeave={(e) =>
                task.isDraggable && (e.currentTarget.style.transform = "scale(1)")
              }
            >
              {task.name}
            </div>
          ))
        ) : (
          <div style={{ ...taskStyle, textAlign: "center", color: "#888" }}>
            No tasks found
          </div>
        )}
      </div>

      {/* Completed Tasks Table */}
      <div
        style={{ ...cardStyle, minWidth: "75%" }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDropTable}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
            marginBottom: "-15px",
            marginTop: "-10px",
          }}
        >
          <div style={{ ...navbarHeaderStyle, flex: 1 }}>
            Total Column: {tasksComplete.length}
          </div>
          <Button
            variant="contained"
            onClick={handleOpen}
            sx={{
              backgroundColor: "#4CAF50",
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              padding: "7px 15px",
            }}
            aria-label="Add Custom Field"
          >
            + Add Custom Field
          </Button>
          <Box sx={{ ...searchContainerStyle, width: "250px", height: "34px" }}>
            <SearchIcon sx={searchIconStyle} />
            <InputBase
              placeholder="Search"
              value={searchQueryComplete}
              onChange={(e) => setSearchQueryComplete(e.target.value)}
              sx={searchInputStyle}
              aria-label="Search completed tasks"
            />
          </Box>
        </div>

        <BootstrapDialog
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={open}
          fullWidth
          maxWidth="md"
        >
          <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
            Add Below Details
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Display Name</InputLabel>
                  <Select
                    name="displayName"
                    label="Display Name"
                    value={newField.displayName}
                    onChange={handleInputChange}
                    aria-label="Select Display Name"
                  >
                    {tasksWIP.map((task) => (
                      <MenuItem key={task.id} value={task.name}>
                        {task.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    label="Category"
                    value={newField.category}
                    onChange={handleInputChange}
                    aria-label="Select Category"
                  >
                    {operators.map((operator) => (
                      <MenuItem key={operator.value} value={operator.value}>
                        {operator.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Function</InputLabel>
                  <Select
                    name="functionName"
                    label="Function"
                    value={newField.functionName}
                    onChange={handleInputChange}
                    aria-label="Select Function"
                  >
                    {tasksWIP.map((task) => (
                      <MenuItem key={task.id} value={task.name}>
                        {task.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} container justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    backgroundColor: "#4CAF50",
                    color: "white",
                    textTransform: "none",
                  }}
                  aria-label="Submit Custom Field"
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </BootstrapDialog>

        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th style={{ ...headerStyle, borderTopLeftRadius: "12px" }}>Column</th>
                <th style={headerStyle}>Display Name</th>
                <th style={headerStyle}>Is Filter</th>
                <th style={headerStyle}>Is Output</th>
                <th style={headerStyle}>Function</th>
                <th style={headerStyle}>Sort</th>
                <th style={headerStyle}>Show Total</th>
                <th style={{ ...headerStyle, borderTopRightRadius: "12px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {tasksComplete.length > 0 ? (
                tasksComplete.map((task) => (
                  <tr
                    key={task.id}
                    draggable={task.isDraggable}
                    onDragStart={(e) => onDragStartTableRow(e, task.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDropTableRow(task.id)}
                    style={tableRowStyle}
                  >
                    <td style={thTdStyle}>{task.name}</td>
                    <td style={thTdStyle}>
                      <input
                        type="text"
                        value={task.displayName || task.name}
                        readOnly
                        style={{
                          height: "38px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          outline: "none",
                          width: "100%",
                        }}
                        aria-label={`Display name for ${task.name}`}
                      />
                    </td>
                    <td style={{ ...thTdStyle, textAlign: "left" }}>
                      <input
                        type="checkbox"
                        style={{
                          transform: "scale(1.3)",
                          margin: "10px",
                          accentColor: "#bfbfbf",
                        }}
                        checked={task.isFilter}
                        readOnly
                        aria-label={`Filter for ${task.name}`}
                      />
                    </td>
                    <td style={{ ...thTdStyle, textAlign: "left" }}>
                      <input
                        type="checkbox"
                        style={{
                          transform: "scale(1.3)",
                          margin: "10px",
                          accentColor: "#bfbfbf",
                        }}
                        checked={task.isOutput}
                        readOnly
                        aria-label={`Output for ${task.name}`}
                      />
                    </td>
                    <td style={thTdStyle}>
                      <input
                        type="text"
                        value={task.functionName || ""}
                        readOnly
                        style={{
                          height: "38px",
                          width: "200px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          padding: "4px 8px",
                          outline: "none",
                        }}
                        aria-label={`Function for ${task.name}`}
                      />
                    </td>
                    <td style={{ ...thTdStyle, textAlign: "left" }}>
                      <input
                        type="checkbox"
                        style={{
                          transform: "scale(1.3)",
                          margin: "10px",
                          accentColor: "#bfbfbf",
                        }}
                        checked={task.isSort}
                        readOnly
                        aria-label={`Sort for ${task.name}`}
                      />
                    </td>
                    <td style={{ ...thTdStyle, textAlign: "left" }}>
                      {(!task.isDraggable || (task.isDraggable && task.isNumeric)) && (
                        <input
                          type="checkbox"
                          style={{
                            transform: "scale(1.3)",
                            margin: "10px",
                            accentColor: "#bfbfbf",
                          }}
                          checked={task.showTotal}
                          readOnly
                          aria-label={`Show total for ${task.name}`}
                        />
                      )}
                    </td>
                    <td style={{ ...thTdStyle, textAlign: "center", width: 32 }}>
                      <img
                        src={delete2}
                        alt="Delete"
                        title="Delete"
                        style={{ width: 25, height: 25, cursor: "pointer" }}
                        onClick={() => onClickDelete(task)}
                        aria-label={`Delete ${task.name}`}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    style={{ ...thTdStyle, textAlign: "center", color: "#888" }}
                  >
                    No tasks found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div >
  );
};

TableWithNewFunctionality.propTypes = {
  showLoador: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapStateToProps = () => ({});
const mapDispatchToProps = { showNotification, showLoador };

export default connect(mapStateToProps, mapDispatchToProps)(TableWithNewFunctionality);